import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../utils/api';

const initialState = {
  issues: [],
  currentIssue: null,
  loading: false,
  error: null,
};

export const fetchIssues = createAsyncThunk('issues/fetchIssues', async (status = 'ALL') => {
  const response = await api.get('/issues/issues', { params: { status } });
  return response.data;
});

export const fetchIssueById = createAsyncThunk(
  'issues/fetchIssueById',
  async (id) => {
    const response = await api.get(`/issues/issues/${id}`);
    return response.data;
  }
);

export const createIssue = createAsyncThunk(
  'issues/createIssue',
  async (issueData) => {
    const response = await api.post('/issues/issues', issueData);
    return response.data;
  }
);

export const updateIssue = createAsyncThunk(
  'issues/updateIssue',
  async ({ id, issueData }) => {
    const response = await api.put(`/issues/issues/${id}`, issueData);
    return response.data;
  }
);

export const deleteIssue = createAsyncThunk(
  'issues/deleteIssue',
  async (id) => {
    await api.delete(`/issues/issues/${id}`);
    return id;
  }
);

const issueSlice = createSlice({
  name: 'issues',
  initialState,
  reducers: {
    clearCurrentIssue(state) {
      state.currentIssue = null;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchIssues.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchIssues.fulfilled, (state, action) => {
        state.loading = false;
        state.issues = action.payload;
      })
      .addCase(fetchIssues.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch issues';
      })
      .addCase(fetchIssueById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchIssueById.fulfilled, (state, action) => {
        state.loading = false;
        state.currentIssue = action.payload;
      })
      .addCase(fetchIssueById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch issue';
      })
      .addCase(createIssue.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createIssue.fulfilled, (state, action) => {
        state.loading = false;
        state.issues.push(action.payload);
      })
      .addCase(createIssue.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to create issue';
      })
      .addCase(updateIssue.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateIssue.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.issues.findIndex((issue) => issue.id === action.payload.id);
        if (index !== -1) {
          state.issues[index] = action.payload;
        }
        if (state.currentIssue && state.currentIssue.id === action.payload.id) {
          state.currentIssue = action.payload;
        }
      })
      .addCase(updateIssue.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to update issue';
      })
      .addCase(deleteIssue.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteIssue.fulfilled, (state, action) => {
        state.loading = false;
        state.issues = state.issues.filter((issue) => issue.id !== action.payload);
        if (state.currentIssue && state.currentIssue.id === action.payload) {
          state.currentIssue = null;
        }
      })
      .addCase(deleteIssue.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to delete issue';
      });
  },
});

export const { clearCurrentIssue } = issueSlice.actions;

export default issueSlice.reducer;
